/**
 * Admin Authentication Hook
 * Handles admin-specific authentication via Supabase
 * Separate from regular user authentication
 */

import { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '@/config/environment';

interface AdminUser {
  id: string;
  email: string;
  role: 'admin';
  permissions: string[];
  isAdmin: boolean;
}

interface AdminAuthState {
  adminUser: AdminUser | null;
  isAdminAuthenticated: boolean;
  isAdminLoading: boolean;
  adminError: string | null;
}

interface UseAdminAuthReturn extends AdminAuthState {
  loginAdmin: (email: string, password: string) => Promise<boolean>;
  logoutAdmin: () => Promise<void>;
  checkAdminAuth: () => Promise<void>;
}

export function useAdminAuth(): UseAdminAuthReturn {
  const [state, setState] = useState<AdminAuthState>({
    adminUser: null,
    isAdminAuthenticated: false,
    isAdminLoading: true,
    adminError: null,
  });

  const checkAdminAuth = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isAdminLoading: true, adminError: null }));

      const response = await fetch(API_CONFIG.getApiUrl('admin/auth/user'), {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setState({
          adminUser: data.user,
          isAdminAuthenticated: !!data.user,
          isAdminLoading: false,
          adminError: null,
        });
      } else {
        setState({
          adminUser: null,
          isAdminAuthenticated: false,
          isAdminLoading: false,
          adminError: null,
        });
      }
    } catch (error) {
      console.error('[ADMIN-AUTH] Check auth error:', error);
      setState({
        adminUser: null,
        isAdminAuthenticated: false,
        isAdminLoading: false,
        adminError: error instanceof Error ? error.message : 'Authentication check failed',
      });
    }
  }, []);

  const loginAdmin = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isAdminLoading: true, adminError: null }));

      const response = await fetch(API_CONFIG.getApiUrl('admin/auth/login'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setState({
          adminUser: data.user,
          isAdminAuthenticated: true,
          isAdminLoading: false,
          adminError: null,
        });
        return true;
      } else {
        setState(prev => ({
          ...prev,
          isAdminLoading: false,
          adminError: data.error || 'Admin login failed',
        }));
        return false;
      }
    } catch (error) {
      console.error('[ADMIN-AUTH] Login error:', error);
      setState(prev => ({
        ...prev,
        isAdminLoading: false,
        adminError: error instanceof Error ? error.message : 'Login failed',
      }));
      return false;
    }
  }, []);

  const logoutAdmin = useCallback(async () => {
    try {
      await fetch(API_CONFIG.getApiUrl('admin/auth/logout'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      setState({
        adminUser: null,
        isAdminAuthenticated: false,
        isAdminLoading: false,
        adminError: null,
      });
    } catch (error) {
      console.error('[ADMIN-AUTH] Logout error:', error);
      // Still clear local state even if logout call fails
      setState({
        adminUser: null,
        isAdminAuthenticated: false,
        isAdminLoading: false,
        adminError: null,
      });
    }
  }, []);

  // Check admin authentication on mount
  useEffect(() => {
    checkAdminAuth();
  }, [checkAdminAuth]);

  return {
    ...state,
    loginAdmin,
    logoutAdmin,
    checkAdminAuth,
  };
}